import { Code } from '@connectrpc/connect';
import { UnitType } from 'gportal-grpc/gportal/api/payment/v1/order_pb';
import { LocalizedError } from 'gportal-grpc/gportal/type/v1/localized_error_pb';
import { GpRegion } from '~/plugins/grpc/universal/types';

export const SECONDS_PER_DAY = 86400;
export const REF_COOKIE_NAME = 'CakeCookie[ref]';
export const CHECKOUT_STORAGE_KEY = 'gp_checkout_config';

// Persisted checkout config is trusted for 24h. Shared by the checkout store
// (restoreFromStorage) and the /checkout/result page so both apply the same
// freshness rule before using a stored snapshot.
export const CHECKOUT_EXPIRY_MS = 24 * 60 * 60 * 1000;

export function isCheckoutConfigExpired(
  timestamp: number | undefined,
): boolean {
  // Reject non-numbers, NaN/Infinity, and future timestamps (clock skew or a
  // tampered store) — all of which would otherwise read as "not expired".
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return true;
  const age = Date.now() - timestamp;
  return age < 0 || age >= CHECKOUT_EXPIRY_MS;
}

/**
 * Version identifier for the Terms & Conditions / Privacy Policy the checkout
 * consent checkbox refers to. Sent with SubmitCheckout and persisted onto the
 * subscription's Chargebee meta_data as the consent evidence trail (Payment
 * Components doesn't populate Chargebee's native consent log). Bump this
 * whenever legal materially changes those documents so a later dispute can be
 * tied to the exact wording the customer accepted. Format is a plain date so
 * it stays human-readable in the billing record.
 */
export const CHECKOUT_TOS_VERSION = '2026-07-14';

export const LATENCY_DANGER_MS = 150;
export const LATENCY_WARNING_MS = 75;

export const DURATION_DISCOUNTS: Record<number, number> = {
  [90 * SECONDS_PER_DAY]: 10,
  [180 * SECONDS_PER_DAY]: 15,
  [365 * SECONDS_PER_DAY]: 20,
};

export function stripSelectPrefix(label: string): string {
  return label.replace(/^select\s+/i, '');
}

export function isHttpsUrl(url: string): boolean {
  return url.startsWith('https://');
}

/**
 * Parse a URL string into a Vue Router location object ({ path, query }).
 * NuxtLink with nuxt-i18n strips query params when given a raw URL string;
 * passing a location object preserves them during route resolution.
 */
export function parseRouteUrl(
  url: string,
): string | { path: string; query: Record<string, string> } {
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return url;
  const path = url.substring(0, qIndex);
  const query: Record<string, string> = {};
  new URLSearchParams(url.substring(qIndex)).forEach((v, k) => {
    query[k] = v;
  });
  return { path, query };
}

export function extractDatacenterSlug(
  latencyEndpointUrl: string | undefined,
): string | null {
  if (!latencyEndpointUrl) return null;
  const match = latencyEndpointUrl.match(/^https:\/\/([^.]+)\./);
  return match ? match[1] : null;
}

/**
 * Returns dynamic Tailwind classes for the selected/unselected/disabled state
 * of option cards used across checkout selectors (duration, unit, location, addon).
 * Static layout classes (border-2, rounded-lg, padding, transitions) stay in `class`.
 */
export function optionCardClass(selected: boolean, disabled = false): string {
  if (disabled) return 'border-depth-3 bg-depth-2 cursor-not-allowed';
  if (selected) return 'border-brand bg-brand/10 cursor-pointer';
  return 'border-depth-3 bg-depth-2 hover:border-brand hover:bg-brand/5 hover:scale-[1.02] cursor-pointer';
}

type TranslateFn = (key: string) => string;

export function getUnitTypeLabel(
  unitType: UnitType | undefined,
  t: TranslateFn,
): string {
  switch (unitType) {
    case UnitType.SLOT:
      return t('checkout/configure/units/slots');
    case UnitType.GB:
      return t('checkout/configure/units/gb_ram');
    default:
      return t('checkout/configure/units/units');
  }
}

export interface CheckoutErrorInfo {
  messageKey: string;
  showRetry: boolean;
  // Set when the customer can clear the error themselves by correcting their
  // billing address, so the caller sends them back to the profile step instead
  // of offering a retry that cannot change anything (TV-1970).
  redirectToProfileStep?: boolean;
  // The Chargebee parameter that was refused, when it named one.
  rejectedField?: string;
}

export type CheckoutError = {
  code: Code;
  message?: string;
  // Structured error details as surfaced by UniversalGrpcClient (raw connect
  // detail wrappers: proto type name + binary payload).
  details?: Array<{ type: string; value: Uint8Array }>;
};

// Every structured backend error arrives the same way: a LocalizedError detail
// carrying a translation key. Decode it once here rather than in each caller.
export function localizedErrorDetail(
  details: CheckoutError['details'],
  key: string,
): LocalizedError | null {
  const detail = details?.find((d) => d.type === LocalizedError.typeName);
  if (!detail) return null;
  try {
    const localized = LocalizedError.fromBinary(detail.value);
    return localized.translationKey === key ? localized : null;
  } catch {
    return null;
  }
}

export function localizedErrorKeyIs(
  details: CheckoutError['details'],
  key: string,
): boolean {
  return localizedErrorDetail(details, key) !== null;
}

// The backend (services/api/pkg/api/errors.go: NewLocationSoldOutError) tags the
// sold-out error with a structured LocalizedError detail carrying this key. This
// is the authoritative signal — robust to message rewording, wrapping, or an
// unrelated FailedPrecondition that happens to contain the words "sold out".
const SOLD_OUT_TRANSLATION_KEY = 'grpc/location_sold_out';

// Fallback free-text match for the transition window before the structured
// detail above ships from the backend (TV-1383/TV-1470). Kept narrow (word
// boundaries) so other FailedPrecondition errors (e.g. WI product validation)
// do not match. Can be removed once the backend change is fully deployed.
const SOLD_OUT_MESSAGE_PATTERN = /\bis sold out\b/i;

export function isSoldOutError(err: CheckoutError | null | undefined): boolean {
  if (!err) return false;
  if (err.code !== Code.FailedPrecondition) return false;
  if (localizedErrorKeyIs(err.details, SOLD_OUT_TRANSLATION_KEY)) return true;
  return (
    typeof err.message === 'string' &&
    SOLD_OUT_MESSAGE_PATTERN.test(err.message)
  );
}

// The backend (services/api/pkg/api/errors.go: NewLocationWrongRegionError) tags a
// cross-region checkout rejection with this key: the selected location is served
// only by the other GPORTAL region (TV-1789). The location is absent from this
// region's order response, so the frontend can't read its region off the location
// object — this structured signal is how it recognises the case and steers the
// user to switch region.
const WRONG_REGION_TRANSLATION_KEY = 'grpc/location_wrong_region';

// Argument the backend sets on the wrong-region detail carrying the location's
// true home region ("EUR"/"INT") — see NewLocationWrongRegionError.
const WRONG_REGION_ARGUMENT_KEY = 'region';

export function isWrongRegionError(
  err: CheckoutError | null | undefined,
): boolean {
  if (!err) return false;
  if (err.code !== Code.FailedPrecondition) return false;
  return localizedErrorKeyIs(err.details, WRONG_REGION_TRANSLATION_KEY);
}

// wrongRegionTargetFromError returns the location's home region exactly as the
// backend named it in the structured error detail. This is authoritative and
// must be preferred over inferring the target from the client's own region: the
// deployment that served the checkout can differ from the user's account/currency
// region (e.g. a shared deep link), and inverting the wrong base would send the
// user to the region where the location still doesn't exist. Returns null when
// this isn't a wrong-region error or the region arg is missing/unrecognised, so
// callers can fall back. See NewLocationWrongRegionError.
export function wrongRegionTargetFromError(
  err: CheckoutError | null | undefined,
): GpRegion | null {
  if (!err) return null;
  if (err.code !== Code.FailedPrecondition) return null;
  const region = localizedErrorDetail(err.details, WRONG_REGION_TRANSLATION_KEY)
    ?.arguments[WRONG_REGION_ARGUMENT_KEY];
  if (region === GpRegion.EUR) return GpRegion.EUR;
  if (region === GpRegion.INT) return GpRegion.INT;
  return null;
}

// Chargebee refused the stored name or address. Retrying sends the identical data
// and fails identically, so the customer has to correct it first.
const BILLING_ADDRESS_TRANSLATION_KEY = 'grpc/billing_address_incomplete';

export function isBillingAddressError(
  err: CheckoutError | null | undefined,
): boolean {
  if (err?.code !== Code.FailedPrecondition) return false;
  return localizedErrorKeyIs(err.details, BILLING_ADDRESS_TRANSLATION_KEY);
}

// Chargebee names the parameter it refused (`billing_address[zip]`), so the form
// can mark that field instead of asking the customer to re-check all six.
export function billingAddressRejectedField(
  err: CheckoutError | null | undefined,
): string {
  if (err?.code !== Code.FailedPrecondition) return '';
  return (
    localizedErrorDetail(err.details, BILLING_ADDRESS_TRANSLATION_KEY)
      ?.arguments['field'] ?? ''
  );
}

export function getCheckoutErrorInfo(err: CheckoutError): CheckoutErrorInfo {
  // Checked before the code switch below, which would otherwise map this
  // FailedPrecondition to the generic "product unavailable".
  if (isBillingAddressError(err)) {
    return {
      messageKey: 'checkout/step/create/error/billing_address',
      showRetry: false,
      redirectToProfileStep: true,
      rejectedField: billingAddressRejectedField(err),
    };
  }
  if (isSoldOutError(err)) {
    return {
      messageKey: 'checkout/step/create/error/sold_out',
      showRetry: false,
    };
  }
  // TV-1789: distinct copy for a cross-region rejection instead of the generic
  // FailedPrecondition "product unavailable" — the handler redirects to the
  // region-switch step, so this is what shows if the redirect ever no-ops.
  if (isWrongRegionError(err)) {
    return {
      messageKey: 'checkout/step/create/error/wrong_region',
      showRetry: false,
    };
  }
  switch (err.code) {
    case Code.AlreadyExists:
      return {
        messageKey: 'checkout/step/create/error/already_subscribed',
        showRetry: false,
      };
    case Code.FailedPrecondition:
      return {
        messageKey: 'checkout/step/create/error/product_unavailable',
        showRetry: false,
      };
    case Code.Unavailable:
      return {
        messageKey: 'checkout/step/create/error/connection_error',
        showRetry: true,
      };
    default:
      return {
        messageKey: 'checkout/step/create/error/message',
        showRetry: true,
      };
  }
}

export function getUnitSuffix(
  unitType: UnitType | undefined,
  t: TranslateFn,
): string {
  switch (unitType) {
    case UnitType.SLOT:
      return t('checkout/configure/units/slots');
    case UnitType.GB:
      return t('checkout/configure/units/gb');
    default:
      return '';
  }
}
